import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useState, useEffect, useRef, useCallback } from "react";
import { Globe, CalendarCheck, Phone, CheckCircle2, ChevronRight, Check } from "lucide-react";
import { getLocalizedPath } from "@/lib/routes";
import { useScrollProgress } from "@/hooks/useScrollProgress";
import { AmbientVideo } from "@/components/fx/AmbientVideo";

const STEP_MS = 2800;
// Portion of the pinned scroll used for the window "landing" before steps begin
const ENTER_END = 0.2;

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(query);
    setMatches(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

export function HeroSection() {
  const { t, language } = useLanguage();
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [paused, setPaused] = useState(false);
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const [tabVisible, setTabVisible] = useState(true);
  const [inView, setInView] = useState(true);
  const sectionRef = useRef<HTMLElement | null>(null);

  // Desktop + motion allowed → the showcase is a pinned, scroll-driven scene
  const pinned = isDesktop && !prefersReducedMotion;

  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVis = () => setTabVisible(!document.hidden);
    onVis();
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) return;
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => setInView(entries[0]?.isIntersecting ?? false), {
      rootMargin: "100px 0px",
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const rotatorsActive = !prefersReducedMotion && tabVisible && inView;
  const isDE = language === "de";

  const highlights = isDE
    ? ["Weniger Unterbrechungen.", "Mehr Überblick im Alltag.", "Weniger Aufwand fürs Team."]
    : ["Fewer interruptions.", "More clarity in everyday practice.", "Less pressure on the team."];

  useEffect(() => {
    if (!rotatorsActive) return;
    const interval = setInterval(() => setHighlightIndex((prev) => (prev + 1) % highlights.length), 3000);
    return () => clearInterval(interval);
  }, [highlights.length, rotatorsActive]);

  const headlineLines = [t("hero.headline.line1"), t("hero.headline.line2"), t("hero.headline.line3")];
  const eyebrowText = t("hero.eyebrow");

  const steps = [
    {
      icon: Globe,
      label: isDE ? "Website" : "Website",
      caption: isDE ? "Patient findet Praxis" : "Patient finds practice",
    },
    {
      icon: CalendarCheck,
      label: isDE ? "Online-Terminbuchung" : "Online booking",
      caption: isDE ? "Termin schnell gebucht" : "Appointment booked quickly",
    },
    {
      icon: Phone,
      label: isDE ? "Anrufe & Nachrichten" : "Calls & messages",
      caption: isDE ? "Auch außerhalb der Öffnungszeiten" : "Also outside opening hours",
    },
    {
      icon: CheckCircle2,
      label: isDE ? "Termin bestätigt" : "Appointment confirmed",
      caption: isDE ? "Im Kalender, ohne Hin und Her" : "On the calendar, no back-and-forth",
    },
  ];

  // Pinned mode: scroll position picks the active step
  const onProgress = useCallback(
    (p: number) => {
      if (!pinned) return;
      const stepP = (p - ENTER_END) / (1 - ENTER_END - 0.04);
      const idx = Math.min(steps.length - 1, Math.max(0, Math.floor(stepP * steps.length)));
      setActiveStep((prev) => (prev === idx ? prev : idx));
    },
    [pinned, steps.length],
  );
  const showcaseRef = useScrollProgress<HTMLDivElement>({ kind: "pin" }, onProgress);

  // Mobile / reduced motion: calm autoplay instead, pausable by hover/focus
  const autoplay = !pinned && rotatorsActive && !paused;
  useEffect(() => {
    if (!autoplay) return;
    const timer = window.setTimeout(() => setActiveStep((prev) => (prev + 1) % steps.length), STEP_MS);
    return () => window.clearTimeout(timer);
  }, [autoplay, activeStep, steps.length]);

  const selectStep = (idx: number) => {
    const el = showcaseRef.current;
    if (pinned && el) {
      // Scroll to the middle of that step's slice of the pinned scene
      const span = el.offsetHeight - window.innerHeight;
      const stepP = ENTER_END + ((idx + 0.5) / steps.length) * (1 - ENTER_END - 0.04);
      const top = el.getBoundingClientRect().top + window.scrollY + span * stepP;
      window.scrollTo({ top, behavior: "smooth" });
    }
    setActiveStep(idx);
  };

  // The film drifts a few pixels against the cursor: depth without distraction
  const onScenePointer = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== "mouse") return;
    const el = e.currentTarget;
    const nx = e.clientX / window.innerWidth - 0.5;
    const ny = e.clientY / window.innerHeight - 0.5;
    el.style.setProperty("--hx", `${(-nx * 18).toFixed(1)}px`);
    el.style.setProperty("--hy", `${(-ny * 12).toFixed(1)}px`);
  }, []);

  const active = steps[activeStep];
  const ActiveIcon = active.icon;
  const confirmed = activeStep === steps.length - 1;
  // Soft colour fields in the detail pane drift to a new spot per step
  const orbSpots = [
    ["10%", "20%", "60%", "55%"],
    ["55%", "5%", "5%", "60%"],
    ["60%", "50%", "15%", "10%"],
    ["25%", "55%", "60%", "10%"],
  ][activeStep];

  return (
    <div className="op-hero-scene relative isolate" onPointerMove={onScenePointer}>
      {/* Ambient film (Higgsfield, 4K source): a calm practice desk where the
          appointment is already confirmed. It stays behind the hero and the
          pinned app window, so the scene never sits on empty white */}
      <div aria-hidden="true" className="pointer-events-none sticky top-0 -z-10 -mb-[100svh] h-[100svh] overflow-hidden">
        <div className="op-hero-film-wrap absolute -inset-6">
          <AmbientVideo
            eager
            src={[
              // 4K HEVC only for large high-density displays (~8.9 MB)
              {
                src: "/media/practice-calm-hevc-2160.mp4",
                type: 'video/mp4; codecs="hvc1"',
                media: "(min-width: 1600px) and (min-resolution: 2dppx)",
              },
              // 1440p HEVC: Safari, iOS, Chrome/Edge with hardware decode (~3.7 MB)
              { src: "/media/practice-calm-hevc-1440.mp4", type: 'video/mp4; codecs="hvc1"' },
              // 1080p H.264 for every other desktop browser (~4 MB); small screens keep the poster
              { src: "/media/practice-calm-1080.mp4", type: "video/mp4", media: "(min-width: 768px)" },
            ]}
            poster="/media/practice-calm-poster.jpg"
            posterSmall="/media/practice-calm-poster-mobile.jpg"
            className="op-hero-film absolute inset-0 h-full w-full"
          />
        </div>
        <div className="op-hero-veil absolute inset-0" />
        <div className="op-hero-veil-text absolute inset-0" />
      </div>

      <section ref={sectionRef} className="op-hero-copy relative overflow-hidden pt-10 md:pt-16 lg:pt-20 pb-10 md:pb-14">
        <div className="container text-center">
          <div className="op-rise">
            <span className="op-eyebrow">{eyebrowText}</span>
          </div>

          <h1
            aria-label={headlineLines.join(" ")}
            className="op-headline mx-auto mt-5 max-w-[16ch] md:max-w-none text-foreground
                       text-[2.6rem] min-[390px]:text-[2.9rem] sm:text-[3.6rem] md:text-[4rem] lg:text-[min(5.5rem,10.5vh)] xl:text-[min(6.5rem,11vh)]"
            style={{ letterSpacing: "-0.045em", lineHeight: 1.02, fontWeight: 700 }}
          >
            {headlineLines.map((line, i) => (
              <span key={i} className="op-hero-line" aria-hidden="true">
                <span className={i === headlineLines.length - 1 ? "op-gradient-text" : undefined}>
                  {line}
                  {i < headlineLines.length - 1 ? " " : ""}
                </span>
              </span>
            ))}
          </h1>

          <div
            aria-hidden="true"
            role="presentation"
            className="op-rise mx-auto mt-6 flex h-[2.5em] max-w-[22ch] items-start justify-center overflow-hidden text-[1.25rem] leading-[1.25] sm:h-[1.3em] sm:max-w-none sm:text-[1.6rem] md:text-[1.75rem] font-semibold tracking-[-0.02em] text-foreground/85"
            style={{ animationDelay: "0.35s" }}
          >
            <span key={highlightIndex} className="block op-rotator-enter">
              {highlights[highlightIndex]}
            </span>
          </div>

          <p
            className="op-rise mx-auto mt-5 max-w-2xl text-[1.06rem] md:text-[1.2rem] leading-[1.5] text-muted-foreground"
            style={{ animationDelay: "0.45s" }}
          >
            {t("hero.description")}
          </p>

          <div
            className="op-rise mt-9 flex flex-col items-center justify-center gap-5 sm:flex-row sm:gap-8"
            style={{ animationDelay: "0.55s" }}
          >
            <Button variant="hero" size="xl" asChild data-magnetic="8">
              <Link to={getLocalizedPath("contact", language)}>{t("hero.cta.primary")}</Link>
            </Button>
            <Link to={getLocalizedPath("consultation", language)} className="op-chevron-link text-[1.06rem] md:text-[1.15rem]">
              <span>{t("hero.cta.secondary")}</span>
              <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
            </Link>
          </div>

          <ul
            className="op-rise mt-10 flex flex-wrap items-center justify-center gap-x-7 gap-y-2 text-sm text-muted-foreground"
            style={{ animationDelay: "0.65s" }}
          >
            {[t("hero.badge1"), t("hero.badge2")].map((badge) => (
              <li key={badge} className="flex items-center gap-2">
                <Check className="h-4 w-4 text-primary" strokeWidth={2.5} />
                {badge}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Showcase — pinned scroll scene on desktop */}
      <div ref={showcaseRef} className={`op-showcase relative ${pinned ? "h-[220vh]" : ""}`}>
        <div className={pinned ? "sticky top-20 flex h-[calc(100vh-5rem)] supports-[height:100dvh]:h-[calc(100dvh-5rem)] items-center" : "pb-16 md:pb-24"}>
          <div className="container">
            <div
              className="op-window op-fade relative mx-auto max-w-[1080px] 2xl:max-w-[1240px] overflow-hidden"
              style={{ animationDelay: "0.5s" }}
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
            >
              {/* Title bar */}
              <div className="op-window-bar">
                <div className="op-traffic" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </div>
                <span className="truncate text-[0.8rem] font-medium text-foreground/70">
                  {isDE ? "Patientenweg · live" : "Patient journey · live"}
                </span>
                <span className="flex items-center justify-end gap-1.5 text-[0.75rem] font-medium text-primary">
                  <span className="relative flex h-2 w-2">
                    {!prefersReducedMotion && <span className="absolute inset-0 animate-ping rounded-full bg-primary/50" />}
                    <span className="relative h-2 w-2 rounded-full bg-primary" />
                  </span>
                  {isDE ? "aktiv" : "active"}
                </span>
              </div>

              <div className="grid md:grid-cols-[minmax(240px,300px)_1fr]">
                {/* Sidebar */}
                <ol className="grid grid-cols-2 gap-1.5 border-b border-black/[0.06] bg-[hsl(240_11%_98%)] p-2.5 md:flex md:flex-col md:gap-1 md:border-b-0 md:border-r md:p-3">
                  {steps.map((s, idx) => (
                    <li key={idx}>
                      <button
                        type="button"
                        className="op-step-item"
                        data-active={idx === activeStep}
                        data-done={idx < activeStep}
                        aria-current={idx === activeStep ? "step" : undefined}
                        onClick={() => selectStep(idx)}
                        onFocus={() => setPaused(true)}
                        onBlur={() => setPaused(false)}
                      >
                        <span className="op-step-icon">
                          {idx < activeStep ? <Check className="h-4 w-4" strokeWidth={2.5} /> : <s.icon className="h-[18px] w-[18px]" />}
                        </span>
                        <span className="min-w-0">
                          <span className="block text-[0.8rem] font-semibold leading-tight md:text-[0.95rem]">{s.label}</span>
                          <span className="op-step-caption mt-0.5 hidden text-[0.8rem] leading-snug text-muted-foreground transition-colors md:block">
                            {s.caption}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ol>

                {/* Detail pane */}
                <div className="relative flex min-h-[300px] flex-col items-center justify-center overflow-hidden px-5 py-9 text-center md:min-h-[440px] md:py-12 [@media(max-height:780px)]:md:min-h-[340px] [@media(max-height:780px)]:md:py-8">
                  <span
                    aria-hidden="true"
                    className="op-orb h-72 w-72 bg-[hsl(152_50%_75%)]"
                    style={{ left: orbSpots[0], top: orbSpots[1] }}
                  />
                  <span
                    aria-hidden="true"
                    className="op-orb h-64 w-64 bg-[hsl(170_45%_82%)]"
                    style={{ left: orbSpots[2], top: orbSpots[3] }}
                  />

                  <div key={activeStep} className="op-detail-enter relative flex flex-col items-center">
                    <span className="relative grid h-20 w-20 md:h-24 md:w-24 place-items-center rounded-[24px] md:rounded-[28px] bg-primary text-primary-foreground shadow-[0_20px_40px_-16px_hsl(152_55%_18%/0.6)]">
                      {!prefersReducedMotion && <span className="op-ring rounded-[34px]" aria-hidden="true" />}
                      <ActiveIcon className="h-10 w-10" strokeWidth={1.75} />
                    </span>
                    <span className="mt-6 md:mt-8 text-sm font-medium tabular-nums text-muted-foreground">
                      0{activeStep + 1} / 0{steps.length}
                    </span>
                    <span className="mt-2 text-[1.75rem] sm:text-3xl lg:text-[2.4rem] font-semibold tracking-[-0.03em] text-foreground leading-tight text-balance">
                      {active.label}
                    </span>
                    <span className="mt-3 text-lg md:text-xl text-muted-foreground">{active.caption}</span>
                  </div>

                  {/* Segmented progress */}
                  <div className="relative mt-8 md:mt-10 grid w-full max-w-sm grid-cols-4 gap-2" aria-hidden="true">
                    {steps.map((_, idx) => (
                      <div key={idx} className={`op-progress-bar ${idx === activeStep && autoplay ? "op-autoplay" : ""}`}>
                        {idx === activeStep && autoplay ? (
                          <span key={`a-${activeStep}`} style={{ ["--dur" as string]: `${STEP_MS}ms` }} />
                        ) : (
                          <span style={{ transform: `scaleX(${idx <= activeStep ? 1 : 0})` }} />
                        )}
                      </div>
                    ))}
                  </div>

                  <span
                    aria-hidden="true"
                    className={`relative mt-6 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-all duration-700 ${
                      confirmed ? "bg-primary text-primary-foreground opacity-100" : "bg-muted text-muted-foreground opacity-60"
                    }`}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {steps[steps.length - 1].label}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
